import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============= TEXT SANITIZATION =============
// jsPDF's default Helvetica is WinAnsi/Latin-1 only.
// Non-Latin1 chars (em-dash, smart quotes, arrows, ™, →, …, ·) corrupt rendering.
// Replace them with safe ASCII equivalents BEFORE passing to doc.text().
function sanitize(text) {
  if (text == null) return '';
  return String(text)
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")  // smart single quotes / prime
    .replace(/[\u201C\u201D\u201E\u2033]/g, '"')         // smart double quotes
    .replace(/\u2014/g, '-')                             // em dash
    .replace(/\u2013/g, '-')                             // en dash
    .replace(/[\u2192\u27A4]/g, '->')                    // right arrow
    .replace(/\u2190/g, '<-')                            // left arrow
    .replace(/\u2191/g, '^')                             // up arrow
    .replace(/\u2193/g, 'v')                             // down arrow
    .replace(/\u2026/g, '...')                           // ellipsis
    .replace(/\u00B7/g, '-')                             // middle dot
    .replace(/\u2022/g, '*')                             // bullet
    .replace(/\u2122/g, '(TM)')                          // trademark
    .replace(/\u00A9/g, '(c)')                           // copyright
    .replace(/\u00AE/g, '(R)')                           // registered
    .replace(/\u00A0/g, ' ')                             // non-breaking space
    .replace(/\u2009|\u200A|\u200B|\u200C|\u200D|\u200E|\u200F/g, '') // thin/zero-width spaces & BIDI marks
    .replace(/\uFEFF/g, '')                              // BOM
    .replace(/[\u2010\u2011\u2012]/g, '-')               // various hyphens
    .replace(/[^\x00-\x7E\u00A0-\u00FF]/g, '?');         // any remaining non-Latin1 -> ?
}

// Sanitize a 2D array of rows for tables
function sanitizeRows(rows) {
  return (rows || []).map(row => (row || []).map(cell => sanitize(cell)));
}

// ============= THEME =============
const THEME = {
  primary: [240, 180, 60],        // amber/gold #f0b43c
  primaryDark: [200, 140, 40],    // darker amber
  dark: [15, 23, 42],             // slate-900
  body: [51, 65, 85],             // slate-700
  muted: [100, 116, 139],         // slate-500
  border: [226, 232, 240],        // slate-200
  bandBg: [254, 243, 199],        // amber-50
};

// ============= LETTERHEAD =============
function drawLetterhead(doc, company) {
  const W = doc.internal.pageSize.getWidth();
  // Top color band
  doc.setFillColor(...THEME.primary);
  doc.rect(0, 0, W, 90, 'F');

  // Logo
  let textX = 32;
  if (company?.logo_url) {
    try {
      doc.addImage(company.logo_url, 'PNG', 32, 18, 54, 54);
      textX = 100;
    } catch { /* ignore bad image */ }
  }

  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(sanitize(company?.name || 'FM Flexi Pro'), textX, 38);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(60, 52, 30);  // dark ink for subtitle on gold band
  const lines = [
    company?.address,
    [company?.contact_email, company?.contact_phone].filter(Boolean).join('  |  ')
  ].filter(Boolean);
  let y = 54;
  lines.forEach(line => {
    doc.text(sanitize(line), textX, y);
    y += 12;
  });

  // Bottom thin accent line
  doc.setDrawColor(...THEME.primaryDark);
  doc.setLineWidth(0.8);
  doc.line(0, 92, W, 92);
}

// ============= FOOTER =============
function drawFooter(doc, company) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(...THEME.border);
    doc.setLineWidth(0.5);
    doc.line(32, H - 48, W - 32, H - 48);

    // Top footer row: company | brand | page
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...THEME.muted);

    const left = sanitize(company?.name ? `${company.name}` : 'FM Flexi Pro');
    const center = sanitize('FM Flexi Pro (TM) by SKG Apps');
    const right = sanitize(`Page ${i} of ${total}`);

    doc.text(left, 32, H - 34);
    doc.text(center, W / 2, H - 34, { align: 'center' });
    doc.text(right, W - 32, H - 34, { align: 'right' });

    // Branding line (centered, smaller, italic)
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(...THEME.primaryDark);
    doc.text(
      sanitize('SGK Apps, branding and a Product of VRV Technical & Technology Solutions FZ LLC'),
      W / 2,
      H - 18,
      { align: 'center' }
    );
  }
}

// ============= PAGE-BREAK HELPER =============
function ensureSpace(doc, currentY, neededHeight, company) {
  const H = doc.internal.pageSize.getHeight();
  if (currentY + neededHeight > H - 50) {
    doc.addPage();
    drawLetterhead(doc, company);
    return 120; // top margin after letterhead
  }
  return currentY;
}

// ============= MAIN: BRANDED REPORT =============
/**
 * Generate a branded PDF report.
 * @param {Object} opts
 *   - title           required
 *   - subtitle        optional
 *   - company         { name, address, contact_email, contact_phone, logo_url }
 *   - sections        [{ heading, paragraph, head: [...], rows: [[...]] }]
 *   - filename        optional
 *   - print           bool — open print dialog instead of save
 *   - view            bool — open PDF in a new tab (no print dialog); falls back to save
 */
export function generateReportPDF({ title, subtitle, company, sections = [], filename, print = false, view = false }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();

  // ----- Page 1 -----
  drawLetterhead(doc, company);

  // Title block
  let y = 130;
  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(sanitize(title), 32, y);

  if (subtitle) {
    y += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...THEME.muted);
    doc.text(sanitize(subtitle), 32, y);
  }
  // Right side: generated timestamp
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...THEME.muted);
  doc.text(sanitize(`Generated: ${new Date().toLocaleString()}`), W - 32, 130, { align: 'right' });

  y += 22;
  // accent under title
  doc.setDrawColor(...THEME.primary);
  doc.setLineWidth(2);
  doc.line(32, y, 80, y);
  y += 18;

  // ----- Sections -----
  sections.forEach((s, idx) => {
    if (s.heading) {
      y = ensureSpace(doc, y, 28, company);
      if (idx > 0) y += 8;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(...THEME.primary);
      doc.text(sanitize(s.heading), 32, y);
      y += 14;
      // small underline
      doc.setDrawColor(...THEME.primary);
      doc.setLineWidth(0.5);
      doc.line(32, y - 8, 32 + Math.min(180, doc.getTextWidth(sanitize(s.heading)) + 10), y - 8);
      y += 4;
    }

    if (s.paragraph) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10.5);
      doc.setTextColor(...THEME.body);
      const safe = sanitize(s.paragraph);
      const split = doc.splitTextToSize(safe, W - 64);
      // Page-break per line group
      const lineHeight = 14;
      split.forEach(line => {
        y = ensureSpace(doc, y, lineHeight, company);
        doc.text(line, 32, y);
        y += lineHeight;
      });
      y += 8;
    }

    if (s.rows && s.rows.length) {
      y = ensureSpace(doc, y, 60, company);
      autoTable(doc, {
        startY: y,
        head: s.head ? [s.head.map(h => sanitize(h))] : undefined,
        body: sanitizeRows(s.rows),
        styles: { font: 'helvetica', fontSize: 9.5, cellPadding: 7, textColor: THEME.body, lineColor: THEME.border, lineWidth: 0.4 },
        headStyles: { fillColor: THEME.primary, textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 32, right: 32 },
        didDrawPage: () => { drawLetterhead(doc, company); }
      });
      y = doc.lastAutoTable.finalY + 14;
    }
  });

  // Footer on every page
  drawFooter(doc, company);

  const safeName = filename || `${(title || 'report').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.pdf`;

  if (print) {
    doc.autoPrint();
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    const win = window.open(url);
    if (!win) doc.save(safeName);
  } else if (view) {
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) {
      // popup blocked → fallback: trigger anchor download
      const a = document.createElement('a');
      a.href = url;
      a.download = safeName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  } else {
    doc.save(safeName);
  }
}

// ============= TABULAR-ONLY (legacy summary) =============
export function generateTablePDF({ title, company, head, rows, filename }) {
  generateReportPDF({
    title,
    company,
    sections: [{ heading: title, head, rows }],
    filename
  });
}

// ============= INVOICE =============
/**
 * Generate an A/R invoice PDF with branded letterhead, bill-to block, line totals.
 *   opts: { invoice, company, payments = [] }
 */
export function generateInvoicePDF({ invoice, company, payments = [] }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  drawLetterhead(doc, company);

  let y = 130;
  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(sanitize('INVOICE'), 32, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...THEME.muted);
  doc.text(sanitize(`# ${invoice.invoice_number || invoice.id}`), 32, y + 16);

  // Right-side meta
  doc.setTextColor(...THEME.body);
  const metaX = W - 32;
  doc.setFontSize(10);
  doc.text(sanitize(`Issue Date:`), metaX - 140, 128);
  doc.text(sanitize(invoice.issue_date || ''), metaX, 128, { align: 'right' });
  doc.text(sanitize(`Due Date:`), metaX - 140, 144);
  doc.text(sanitize(invoice.due_date || ''), metaX, 144, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.text(sanitize(`Status:`), metaX - 140, 160);
  doc.text(sanitize((invoice.status || 'DRAFT').toUpperCase()), metaX, 160, { align: 'right' });

  // Bill-to block
  y = 180;
  doc.setDrawColor(...THEME.border);
  doc.setFillColor(...THEME.bandBg);
  doc.roundedRect(32, y, W - 64, 60, 4, 4, 'FD');
  doc.setTextColor(...THEME.muted);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(sanitize('BILL TO'), 44, y + 18);
  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(sanitize(invoice.customer_name || ''), 44, y + 34);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...THEME.body);
  if (invoice.customer_email) doc.text(sanitize(invoice.customer_email), 44, y + 48);

  // Description table
  y += 80;
  const currency = invoice.currency || company?.currency_symbol || 'AED';
  const rows = [[sanitize(invoice.description || 'Facilities management services'), 1, `${currency} ${(invoice.amount || 0).toFixed(2)}`, `${currency} ${(invoice.amount || 0).toFixed(2)}`]];
  autoTable(doc, {
    startY: y,
    head: [['Description', 'Qty', 'Unit', 'Amount']],
    body: rows,
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 8, textColor: THEME.body, lineColor: THEME.border, lineWidth: 0.4 },
    headStyles: { fillColor: THEME.primary, textColor: THEME.dark, fontStyle: 'bold' },
    columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    margin: { left: 32, right: 32 }
  });
  y = doc.lastAutoTable.finalY + 12;

  // Totals
  const paid = invoice.paid_amount || 0;
  const total = invoice.total_amount || ((invoice.amount || 0) + (invoice.tax_amount || 0));
  const due = Math.max(total - paid, 0);
  const labelX = W - 200;
  const valueX = W - 32;
  const line = (l, v, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(...(bold ? THEME.dark : THEME.body));
    doc.text(sanitize(l), labelX, y);
    doc.text(sanitize(`${currency} ${Number(v).toFixed(2)}`), valueX, y, { align: 'right' });
    y += 14;
  };
  doc.setFontSize(10);
  line('Subtotal', invoice.amount || 0);
  if (invoice.tax_amount) line('Tax', invoice.tax_amount);
  doc.setDrawColor(...THEME.border);
  doc.line(labelX, y - 6, valueX, y - 6);
  line('Total', total, true);
  line('Paid', paid);
  doc.setFillColor(...THEME.bandBg);
  doc.rect(labelX - 6, y - 12, valueX - labelX + 12, 22, 'F');
  line('Amount Due', due, true);

  // Payments applied
  if (payments.length) {
    y += 14;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...THEME.primary);
    doc.text(sanitize('Payments Applied'), 32, y);
    y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Date', 'Method', 'Reference', 'Amount']],
      body: payments.map(p => [p.paid_at, p.method, p.reference || '-', `${currency} ${Number(p.amount || 0).toFixed(2)}`]),
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 6, textColor: THEME.body },
      headStyles: { fillColor: THEME.bandBg, textColor: THEME.dark },
      margin: { left: 32, right: 32 }
    });
  }

  if (invoice.notes) {
    y = (doc.lastAutoTable?.finalY || y) + 18;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...THEME.muted);
    doc.text(sanitize(`Notes: ${invoice.notes}`), 32, y);
  }

  drawFooter(doc, company);
  doc.save(`invoice_${(invoice.invoice_number || invoice.id).replace(/[^a-z0-9]+/gi, '_')}.pdf`);
}

// ============= PAYSLIP =============
/**
 * Generate a payroll slip PDF with earnings/deductions breakdown.
 *   opts: { payroll, company }
 */
export function generatePayslipPDF({ payroll, company }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  drawLetterhead(doc, company);

  const currency = company?.currency_symbol || 'AED';
  const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  let y = 130;
  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(sanitize('PAYSLIP'), 32, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...THEME.muted);
  doc.text(sanitize(`${monthNames[payroll.month] || payroll.month} ${payroll.year}`), 32, y + 16);

  // Employee block
  y = 170;
  doc.setDrawColor(...THEME.border);
  doc.setFillColor(...THEME.bandBg);
  doc.roundedRect(32, y, W - 64, 52, 4, 4, 'FD');
  doc.setTextColor(...THEME.muted);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(sanitize('EMPLOYEE'), 44, y + 18);
  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(sanitize(payroll.user_name || payroll.user_id || '-'), 44, y + 36);
  // Status badge on right
  const statusText = (payroll.status || 'PENDING').toUpperCase();
  doc.setFillColor(...THEME.primary);
  const sw = doc.getTextWidth(statusText) + 20;
  doc.roundedRect(W - 32 - sw, y + 16, sw, 18, 9, 9, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...THEME.dark);
  doc.text(statusText, W - 32 - sw / 2, y + 28, { align: 'center' });

  // Earnings and deductions tables side by side
  y += 70;
  const colW = (W - 76) / 2;

  autoTable(doc, {
    startY: y,
    head: [['Earnings', 'Amount']],
    body: [
      ['Basic Salary', `${currency} ${Number(payroll.basic_salary || 0).toFixed(2)}`],
      ['Allowances', `${currency} ${Number(payroll.allowances || 0).toFixed(2)}`],
      ['Overtime', `${currency} ${Number(payroll.overtime || 0).toFixed(2)}`],
    ],
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 7, textColor: THEME.body, lineColor: THEME.border, lineWidth: 0.4 },
    headStyles: { fillColor: THEME.primary, textColor: THEME.dark },
    columnStyles: { 1: { halign: 'right' } },
    margin: { left: 32, right: 32 + colW + 12 },
    tableWidth: colW
  });
  const earnEndY = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    head: [['Deductions', 'Amount']],
    body: [
      ['Deductions / Leave / Loss', `${currency} ${Number(payroll.deductions || 0).toFixed(2)}`],
    ],
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 7, textColor: THEME.body, lineColor: THEME.border, lineWidth: 0.4 },
    headStyles: { fillColor: [194, 65, 12], textColor: 255 },  // orange-700 for deductions
    columnStyles: { 1: { halign: 'right' } },
    margin: { left: 32 + colW + 12, right: 32 },
    tableWidth: colW
  });
  const dedEndY = doc.lastAutoTable.finalY;

  y = Math.max(earnEndY, dedEndY) + 22;

  // Net pay box
  doc.setFillColor(...THEME.primary);
  doc.roundedRect(32, y, W - 64, 44, 6, 6, 'F');
  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(sanitize('NET PAY'), 48, y + 28);
  doc.setFontSize(18);
  doc.text(sanitize(`${currency} ${Number(payroll.net_salary || 0).toFixed(2)}`), W - 48, y + 28, { align: 'right' });

  if (payroll.paid_at) {
    y += 56;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...THEME.muted);
    doc.text(sanitize(`Paid on: ${payroll.paid_at.slice(0, 10)}`), 32, y);
  }
  if (payroll.notes) {
    y += 14;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...THEME.muted);
    doc.text(sanitize(`Notes: ${payroll.notes}`), 32, y);
  }

  // Signatures
  y = Math.max(y, doc.internal.pageSize.getHeight() - 130);
  doc.setDrawColor(...THEME.border);
  doc.line(60, y, 210, y);
  doc.line(W - 210, y, W - 60, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...THEME.muted);
  doc.text(sanitize('Employee Signature'), 135, y + 14, { align: 'center' });
  doc.text(sanitize('Authorized Signatory'), W - 135, y + 14, { align: 'center' });

  drawFooter(doc, company);
  doc.save(`payslip_${(payroll.user_name || 'employee').replace(/[^a-z0-9]+/gi, '_')}_${payroll.year}_${String(payroll.month).padStart(2, '0')}.pdf`);
}
